import { supabase } from '@/integrations/supabase/client';
import { LEAVE_TYPES } from '@/constants/leaveTypes';

export type PayrollExportFormat = 'csv' | 'json';

// ── Types ──────────────────────────────────────────────────────────────

interface PayslipExportData {
  id: string;
  profile_id: string;
  employee_name: string;
  employee_email: string;
  total_hours: number;
  gross_pay: number;
  total_deductions: number;
  tax_withheld: number;
  net_pay: number;
  line_items?: LineItemExportData[];
  // Day-level metrics (JSON only)
  working_days?: number;
  approved_paid_leave_days?: number;
  approved_unpaid_leave_days?: number;
  days_off?: number;
}

interface LineItemExportData {
  description: string;
  category: string | null;
  gross_amount: number;
  net_amount: number;
  hours_applied: number | null;
  rate_applied: number | null;
  line_item_type: string;
  calculation_method: string;
}

interface PayrollExportResult {
  content: string;
  filename: string;
  mimeType: string;
}

interface EmployeeLeaveDayCounts {
  paid_leave_days: number;
  unpaid_leave_days: number;
}

// ── Leave category lookup ──────────────────────────────────────────────

/** Set of leave_type values classified as unpaid */
const UNPAID_LEAVE_TYPES = new Set(
  LEAVE_TYPES.filter(t => t.category === 'unpaid').map(t => t.value)
);

/** Returns true if a leave_type is unpaid; paid + special are treated as paid */
function isUnpaidLeaveType(leaveType: string): boolean {
  return UNPAID_LEAVE_TYPES.has(leaveType);
}

// ── Data fetching ──────────────────────────────────────────────────────

async function fetchPayslipsForExport(
  runId: string,
  payslipIds?: string[]
): Promise<PayslipExportData[]> {
  let query = supabase
    .from('payslips')
    .select(`
      id,
      profile_id,
      total_hours,
      gross_pay,
      total_deductions,
      tax_withheld,
      net_pay,
      deleted_employee_name,
      deleted_employee_email,
      profile:profile_id(first_name, last_name, email)
    `)
    .eq('payroll_run_id', runId)
    .order('created_at', { ascending: true });

  if (payslipIds && payslipIds.length > 0) {
    query = query.in('id', payslipIds);
  }

  const { data, error } = await query;
  if (error) throw new Error(`Failed to fetch payslips: ${error.message}`);

  return (data || []).map((p: any) => ({
    id: p.id,
    profile_id: p.profile_id,
    employee_name: p.profile
      ? `${p.profile.first_name || ''} ${p.profile.last_name || ''}`.trim()
      : p.deleted_employee_name || 'Unknown',
    employee_email: p.profile?.email || p.deleted_employee_email || 'N/A',
    total_hours: Number(p.total_hours || 0),
    gross_pay: Number(p.gross_pay || 0),
    total_deductions: Number(p.total_deductions || 0),
    tax_withheld: Number(p.tax_withheld || 0),
    net_pay: Number(p.net_pay || 0),
  }));
}

async function fetchLineItemsForPayslips(
  payslipIds: string[]
): Promise<Map<string, LineItemExportData[]>> {
  const result = new Map<string, LineItemExportData[]>();
  if (payslipIds.length === 0) return result;

  const { data, error } = await supabase
    .from('payslip_line_items')
    .select('payslip_id, description, category, gross_amount, net_amount, hours_applied, rate_applied, line_item_type, calculation_method')
    .in('payslip_id', payslipIds)
    .order('display_order', { ascending: true });

  if (error) throw new Error(`Failed to fetch line items: ${error.message}`);

  for (const item of data || []) {
    const existing = result.get(item.payslip_id) || [];
    existing.push({
      description: item.description,
      category: item.category,
      gross_amount: Number(item.gross_amount || 0),
      net_amount: Number(item.net_amount || 0),
      hours_applied: item.hours_applied ? Number(item.hours_applied) : null,
      rate_applied: item.rate_applied ? Number(item.rate_applied) : null,
      line_item_type: item.line_item_type,
      calculation_method: item.calculation_method,
    });
    result.set(item.payslip_id, existing);
  }

  return result;
}

async function fetchRunForExport(runId: string) {
  const { data, error } = await supabase
    .from('payroll_runs')
    .select('*')
    .eq('id', runId)
    .single();

  if (error) throw new Error(`Failed to fetch payroll run: ${error.message}`);
  return data;
}

// ── Day-level analytics helpers ────────────────────────────────────────

interface BusinessHoursConfig {
  [key: string]: { enabled: boolean; start: string; end: string };
}

/**
 * Compute the number of business days in a date range.
 * Uses company business_hours config (which weekdays are enabled) and holidays.
 */
/**
 * Compute calendar days and business days in a date range.
 */
async function computePeriodDayCounts(
  companyId: string,
  startDate: string,
  endDate: string
): Promise<{ calendarDays: number; businessDays: number; timezone: string; avgDailyHours: number }> {
  const { data: settings } = await supabase
    .from('company_settings')
    .select('business_hours, holidays, timezone')
    .eq('company_id', companyId)
    .single();

  const businessHours: BusinessHoursConfig = (settings?.business_hours as BusinessHoursConfig) || {
    monday: { enabled: true, start: '09:00', end: '17:00' },
    tuesday: { enabled: true, start: '09:00', end: '17:00' },
    wednesday: { enabled: true, start: '09:00', end: '17:00' },
    thursday: { enabled: true, start: '09:00', end: '17:00' },
    friday: { enabled: true, start: '09:00', end: '17:00' },
    saturday: { enabled: false, start: '09:00', end: '17:00' },
    sunday: { enabled: false, start: '09:00', end: '17:00' },
  };

  const holidays = new Set<string>(
    ((settings as any)?.holidays as string[]) || []
  );

  const timezone = (settings as any)?.timezone || 'UTC';

  const dayNames = ['sunday', 'monday', 'tuesday', 'wednesday', 'thursday', 'friday', 'saturday'];

  // Compute avg daily hours for part-day leave conversion
  let totalConfigHours = 0;
  let enabledDayCount = 0;
  for (const day of dayNames) {
    const cfg = businessHours[day];
    if (cfg?.enabled) {
      const [sh, sm] = cfg.start.split(':').map(Number);
      const [eh, em] = cfg.end.split(':').map(Number);
      const h = (eh * 60 + em - (sh * 60 + sm)) / 60;
      if (h > 0) {
        totalConfigHours += h;
        enabledDayCount++;
      }
    }
  }
  const avgDailyHours = enabledDayCount > 0 ? totalConfigHours / enabledDayCount : 8;

  // Iterate each calendar date in the period
  const [sy, sm, sd] = startDate.split('-').map(Number);
  const [ey, em, ed] = endDate.split('-').map(Number);
  const cursor = new Date(Date.UTC(sy, sm - 1, sd));
  const endRef = new Date(Date.UTC(ey, em - 1, ed));

  let calendarDays = 0;
  let businessDays = 0;
  while (cursor <= endRef) {
    calendarDays++;
    const dateStr = cursor.toISOString().split('T')[0];
    const dayOfWeek = cursor.getUTCDay();
    const dayName = dayNames[dayOfWeek];
    const isEnabled = businessHours[dayName]?.enabled ?? false;
    const isHoliday = holidays.has(dateStr);

    if (isEnabled && !isHoliday) {
      businessDays++;
    }

    cursor.setUTCDate(cursor.getUTCDate() + 1);
  }

  return { calendarDays, businessDays, timezone, avgDailyHours };
}

/**
 * Batch-compute working days (distinct dates with approved time entries) per employee.
 */
async function computeWorkingDaysPerEmployee(
  profileIds: string[],
  startDate: string,
  endDate: string,
  timezone: string
): Promise<Map<string, number>> {
  const result = new Map<string, number>();
  if (profileIds.length === 0) return result;

  // Initialize all to 0
  for (const id of profileIds) result.set(id, 0);

  const { data, error } = await supabase
    .from('time_entries')
    .select('profile_id, clock_in')
    .in('profile_id', profileIds)
    .eq('status', 'approved')
    .not('clock_out', 'is', null)
    .gte('clock_in', `${startDate}T00:00:00`)
    .lte('clock_in', `${endDate}T23:59:59`);

  if (error) {
    console.error('[payrollExport] Failed to fetch time entries for working days:', error);
    return result;
  }

  // Group by (profile_id, date) using company timezone
  const seen = new Set<string>();
  for (const entry of data || []) {
    const dateStr = new Date(entry.clock_in).toLocaleDateString('en-CA', { timeZone: timezone });
    const key = `${entry.profile_id}|${dateStr}`;
    if (!seen.has(key)) {
      seen.add(key);
      result.set(entry.profile_id, (result.get(entry.profile_id) || 0) + 1);
    }
  }

  return result;
}

/**
 * Batch-compute approved leave days per employee, split into paid and unpaid.
 * Prorates multi-period leave to only count days within the pay period.
 * Part-day leave (requested_hours only) converted to fractional days.
 */
async function computeLeaveDaysPerEmployee(
  profileIds: string[],
  startDate: string,
  endDate: string,
  avgDailyHours: number
): Promise<Map<string, EmployeeLeaveDayCounts>> {
  const result = new Map<string, EmployeeLeaveDayCounts>();
  if (profileIds.length === 0) return result;

  for (const id of profileIds) {
    result.set(id, { paid_leave_days: 0, unpaid_leave_days: 0 });
  }

  const { data, error } = await supabase
    .from('leave_requests')
    .select('profile_id, leave_type, start_date, end_date, requested_days, requested_hours')
    .in('profile_id', profileIds)
    .eq('status', 'approved')
    .lte('start_date', endDate)
    .gte('end_date', startDate);

  if (error) {
    console.error('[payrollExport] Failed to fetch leave requests:', error);
    return result;
  }

  for (const leave of data || []) {
    const counts = result.get(leave.profile_id);
    if (!counts) continue;

    let days = 0;

    if (leave.requested_hours && leave.requested_hours > 0 && (!leave.requested_days || leave.requested_days === 0)) {
      // Part-day leave: convert hours to fractional days
      days = leave.requested_hours / avgDailyHours;
    } else if (leave.requested_days && leave.requested_days > 0) {
      // Full-day leave: prorate to period boundaries
      const leaveStart = leave.start_date;
      const leaveEnd = leave.end_date;
      const periodStart = startDate;
      const periodEnd = endDate;

      // Clamp leave range to period
      const effectiveStart = leaveStart > periodStart ? leaveStart : periodStart;
      const effectiveEnd = leaveEnd < periodEnd ? leaveEnd : periodEnd;

      // Count calendar days in the clamped range
      const [s1y, s1m, s1d] = effectiveStart.split('-').map(Number);
      const [e1y, e1m, e1d] = effectiveEnd.split('-').map(Number);
      const startMs = Date.UTC(s1y, s1m - 1, s1d);
      const endMs = Date.UTC(e1y, e1m - 1, e1d);
      const calendarDaysInPeriod = Math.max(0, Math.round((endMs - startMs) / (86400000)) + 1);

      // Total calendar days of the full leave
      const [ls1y, ls1m, ls1d] = leaveStart.split('-').map(Number);
      const [le1y, le1m, le1d] = leaveEnd.split('-').map(Number);
      const fullStartMs = Date.UTC(ls1y, ls1m - 1, ls1d);
      const fullEndMs = Date.UTC(le1y, le1m - 1, le1d);
      const totalCalendarDays = Math.max(1, Math.round((fullEndMs - fullStartMs) / 86400000) + 1);

      // Prorate: (days_in_period / total_calendar_days) * requested_days
      days = (calendarDaysInPeriod / totalCalendarDays) * leave.requested_days;
    }

    if (days > 0) {
      if (isUnpaidLeaveType(leave.leave_type)) {
        counts.unpaid_leave_days += days;
      } else {
        // paid + special both go to paid bucket
        counts.paid_leave_days += days;
      }
    }
  }

  // Round to 2 decimal places
  for (const [id, counts] of result) {
    counts.paid_leave_days = Math.round(counts.paid_leave_days * 100) / 100;
    counts.unpaid_leave_days = Math.round(counts.unpaid_leave_days * 100) / 100;
  }

  return result;
}

// ── CSV / JSON generators ──────────────────────────────────────────────

function escapeCSV(value: string): string {
  return `"${value.toString().replace(/"/g, '""')}"`;
}

function generateCSV(
  run: any,
  payslips: PayslipExportData[],
  periodDays: { calendarDays: number; businessDays: number }
): PayrollExportResult {
  const headers = [
    'Run Number',
    'Period Start',
    'Period End',
    'Calendar Days in Period',
    'Business Days in Period',
    'Employee Name',
    'Employee Email',
    'Total Hours',
    'Gross Pay',
    'Total Deductions',
    'Tax Withheld',
    'Net Pay',
    'Working Days',
    'Approved Paid Leave Days',
    'Approved Unpaid Leave Days',
    'Days Off',
    'Line Item Description',
    'Line Item Category',
    'Line Item Type',
    'Line Item Calculation Method',
    'Line Item Gross Amount',
    'Line Item Net Amount',
    'Line Item Hours Applied',
    'Line Item Rate Applied',
  ];

  const rows: string[][] = [];

  for (const p of payslips) {
    const baseRow = [
      escapeCSV(run.run_number),
      escapeCSV(run.start_date),
      escapeCSV(run.end_date),
      periodDays.calendarDays.toString(),
      periodDays.businessDays.toString(),
      escapeCSV(p.employee_name),
      escapeCSV(p.employee_email),
      p.total_hours.toFixed(2),
      p.gross_pay.toFixed(2),
      p.total_deductions.toFixed(2),
      p.tax_withheld.toFixed(2),
      p.net_pay.toFixed(2),
      (p.working_days ?? 0).toString(),
      (p.approved_paid_leave_days ?? 0).toString(),
      (p.approved_unpaid_leave_days ?? 0).toString(),
      (p.days_off ?? 0).toString(),
    ];

    const lineItems = p.line_items || [];
    if (lineItems.length === 0) {
      // One row per employee with empty line item columns
      rows.push([...baseRow, '', '', '', '', '', '', '', '']);
    } else {
      for (const li of lineItems) {
        rows.push([
          ...baseRow,
          escapeCSV(li.description),
          escapeCSV(li.category || ''),
          escapeCSV(li.line_item_type),
          escapeCSV(li.calculation_method),
          li.gross_amount.toFixed(2),
          li.net_amount.toFixed(2),
          li.hours_applied != null ? li.hours_applied.toFixed(2) : '',
          li.rate_applied != null ? li.rate_applied.toFixed(2) : '',
        ]);
      }
    }
  }

  const content = [headers.join(','), ...rows.map((r) => r.join(','))].join('\n');
  const filename = `payroll_${run.run_number}_${run.start_date}_to_${run.end_date}.csv`;

  return { content, filename, mimeType: 'text/csv;charset=utf-8;' };
}

function generateJSON(
  run: any,
  payslips: PayslipExportData[],
  periodDays: { calendarDays: number; businessDays: number }
): PayrollExportResult {
  const exportData = {
    run: {
      id: run.id,
      run_number: run.run_number,
      start_date: run.start_date,
      end_date: run.end_date,
      status: run.status,
      total_employees_selected: run.total_employees_selected,
      payslips_generated: run.payslips_generated,
      total_gross_pay: Number(run.total_gross_pay || 0),
      total_net_pay: Number(run.total_net_pay || 0),
      total_hours: Number(run.total_hours || 0),
      calendar_days_in_period: periodDays.calendarDays,
      business_days_in_period: periodDays.businessDays,
      created_at: run.created_at,
      completed_at: run.completed_at,
    },
    payslips: payslips.map((p) => ({
      employee_name: p.employee_name,
      employee_email: p.employee_email,
      total_hours: p.total_hours,
      gross_pay: p.gross_pay,
      total_deductions: p.total_deductions,
      tax_withheld: p.tax_withheld,
      net_pay: p.net_pay,
      working_days: p.working_days ?? 0,
      approved_paid_leave_days: p.approved_paid_leave_days ?? 0,
      approved_unpaid_leave_days: p.approved_unpaid_leave_days ?? 0,
      days_off: p.days_off ?? 0,
      line_items: p.line_items || [],
    })),
    exported_at: new Date().toISOString(),
  };

  const content = JSON.stringify(exportData, null, 2);
  const filename = `payroll_${run.run_number}_${run.start_date}_to_${run.end_date}.json`;

  return { content, filename, mimeType: 'application/json' };
}

// ── Download helper ────────────────────────────────────────────────────

function downloadFile(result: PayrollExportResult) {
  const blob = new Blob([result.content], { type: result.mimeType });
  const url = URL.createObjectURL(blob);
  const a = document.createElement('a');
  a.style.display = 'none';
  a.href = url;
  a.download = result.filename;
  document.body.appendChild(a);
  a.click();
  URL.revokeObjectURL(url);
  document.body.removeChild(a);
}

// ── Main export function ───────────────────────────────────────────────

export async function exportPayrollRun(
  runId: string,
  exportFormat: PayrollExportFormat,
  payslipIds?: string[]
): Promise<{ payslipCount: number }> {
  // Fetch run and payslips in parallel
  const [run, payslips] = await Promise.all([
    fetchRunForExport(runId),
    fetchPayslipsForExport(runId, payslipIds),
  ]);

  if (payslips.length === 0) {
    throw new Error('No payslips to export');
  }

  let periodDays = { calendarDays: 0, businessDays: 0 };

  // Enrich payslips with line items + day-level metrics for both formats
  const ids = payslips.map((p) => p.id);
  const profileIds = [...new Set(payslips.map((p) => p.profile_id))];

  const [lineItemsMap, dayResult] = await Promise.all([
    fetchLineItemsForPayslips(ids),
    computePeriodDayCounts(run.company_id, run.start_date, run.end_date),
  ]);

  periodDays = { calendarDays: dayResult.calendarDays, businessDays: dayResult.businessDays };

  const [workingDaysMap, leaveDaysMap] = await Promise.all([
    computeWorkingDaysPerEmployee(profileIds, run.start_date, run.end_date, dayResult.timezone),
    computeLeaveDaysPerEmployee(profileIds, run.start_date, run.end_date, dayResult.avgDailyHours),
  ]);

  for (const payslip of payslips) {
    payslip.line_items = lineItemsMap.get(payslip.id) || [];

    const workingDays = workingDaysMap.get(payslip.profile_id) || 0;
    const leave = leaveDaysMap.get(payslip.profile_id) || { paid_leave_days: 0, unpaid_leave_days: 0 };

    payslip.working_days = workingDays;
    payslip.approved_paid_leave_days = leave.paid_leave_days;
    payslip.approved_unpaid_leave_days = leave.unpaid_leave_days;
    payslip.days_off = Math.max(
      0,
      Math.round((dayResult.calendarDays - workingDays - leave.paid_leave_days - leave.unpaid_leave_days) * 100) / 100
    );
  }

  const result =
    exportFormat === 'csv'
      ? generateCSV(run, payslips, periodDays)
      : generateJSON(run, payslips, periodDays);

  downloadFile(result);

  return { payslipCount: payslips.length };
}
